import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  BasicModal,
  Button,
  RadioChangeEvent,
  Step,
  Stepper,
} from "@life-line-lab/components-react";
import { omitBy, isUndefined, isEmpty, find, isNil } from "lodash";
// antd
import { ArrowLeftOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Alert } from "antd";
// shared
import Upload from "Components/shared/upload/upload";
import UserDetails from "./stepper-contents/user-details/user-details";
import EmploymentDetails from "./stepper-contents/employment-details/employment-details";
import PaymentDetails from "./stepper-contents/payment-details/payment-details";
import EmergencyContacts from "./stepper-contents/emergency-contacts/emergency-contacts";
import Attachments from "./stepper-contents/attachments/attachments";
// schemas
import UserDetailsSchema from "./stepper-contents/user-details/user-details.validation";
import EmploymentDetailsSchema from "./stepper-contents/employment-details/employment-details.validation";
import PaymentDetailsSchema from "./stepper-contents/payment-details/payment-details.validation";
import EmergencyContactsSchema from "./stepper-contents/emergency-contacts/emergency-contacts.validation";
import AttachmentsSchema from "./stepper-contents/attachments/attachments.validation";
// utils
import { errorHandler } from "Utils/index";
// types
import {
  OrgType,
  PhoneInputType,
  Option,
  AccountRole,
  FileTypeFilePayload,
} from "Types/common.types";
import {
  FetchedUserData,
  UserCreationInput,
  UserProfileCategoriesTab,
} from "Types/user.types";
// hooks
import { useSession } from "Hooks/index";
// services
import { fetchCustomer } from "Services/customer.service";
import { fetchCustomerLocationList } from "Services/location.service";
// scss
import "./user-create-form.scss";
// helpers
import { getBase64, toaster } from "Helpers/index";
import { generateSteps } from "Helpers/generate-stepper-steps";

interface UserCreationProps {
  onBack: () => void;
  onSubmitCreationForm?: (data: UserCreationInput) => void;
  onSubmitEditForm?: (data: UserCreationInput) => void;
  userDetails?: FetchedUserData;
  isLoading: boolean;
}

const UserCreationForm: React.FC<UserCreationProps> = ({
  onBack,
  onSubmitCreationForm,
  onSubmitEditForm,
  userDetails,
  isLoading,
}) => {
  const initialInput: UserCreationInput = {
    idPassportType: "NRIC",
    mobileNo: {
      dialCode: "60",
    },
    emergencyContactNo: {
      dialCode: "60",
    },
    acceptTerms: false,
  };
  const initialError = {};
  const initialUploadModal = {
    title: "",
    type: "",
    format: "",
    open: false,
  };
  const initialSteps: Step[] = generateSteps<Step>(5);
  const editFormType = [
    UserProfileCategoriesTab.USER_DETAILS,
    UserProfileCategoriesTab.EMPLOYMENT_DETAILS,
    UserProfileCategoriesTab.PAYMENT_DETAILS,
    UserProfileCategoriesTab.EMERGENCY_CONTACTS,
    UserProfileCategoriesTab.ATTACHMENTS,
    // if more steps are needed, add steps name here, otherwise on edit it won't direct to the right Step component.
  ];

  const { t } = useTranslation();
  const { org } = useSession();
  const { formType } =
    useParams<{ formType: UserProfileCategoriesTab }>() || "";
  const isOrgCustomer = org.orgType === OrgType.CUSTOMER;

  const [input, setInput] = useState<UserCreationInput>(initialInput);
  const [error, setError] = useState<any>(initialError);
  const [locOptions, setLocOptions] = useState<Option[]>([]);
  const [steps, setSteps] = useState<Step[]>(initialSteps);
  const [current, setCurrent] = useState<number>(0);
  const [uploadFileList, setUploadFileList] = useState<any>([]);
  const [isShowAlert, setIsShowAlert] = useState<boolean>(false);
  const [uploadModal, setUploadModal] = useState<any>(initialUploadModal);

  useEffect(() => {
    if (isOrgCustomer) {
      handleFetchCustomerData();
      handleFetchCustomerLocationsData();
    }
  }, []);

  // to direct user to same tab that they are viewing
  useEffect(() => {
    if (formType && editFormType.includes(formType)) {
      setCurrent(editFormType.indexOf(formType));
    }
  }, [formType]);

  // to map data into input while editing user (when userDetails has data)
  useEffect(() => {
    if (!isEmpty(userDetails)) {
      setInput((prevInput) => ({
        ...prevInput,
        ...userDetails,
        ...userDetails?.profile,
        ...userDetails?.userRole,
      }));
    }
  }, [userDetails]);

  // enter common props here
  const commonStepperProps = {
    input: input,
    error: error,
    onTextInputChange(event: React.ChangeEvent<HTMLInputElement>) {
      handleTextInputChange(event);
    },
  };

  // validation schema for each stepper components
  const validationSchemas = [
    UserDetailsSchema,
    EmploymentDetailsSchema,
    PaymentDetailsSchema,
    EmergencyContactsSchema,
    AttachmentsSchema,
  ];

  const handleFetchCustomerData = async () => {
    const responseData = await fetchCustomer(org._id);

    if (responseData && !responseData.error) {
      setInput({ ...input, company: responseData.customerName });
    }
  };

  const handleFetchCustomerLocationsData = async () => {
    const responseData = await fetchCustomerLocationList(
      { tempDelete: false },
      org._id,
    );

    if (responseData && !responseData.error) {
      const options = responseData.map((location) => {
        return {
          label: location.locationName ?? "Primary Address",
          value: location._id,
        };
      });
      if (!isEmpty(options)) setLocOptions(options);
    }
  };

  const prevStepperComponent = () => {
    const prevStep = find(steps, (_, index) => {
      return index === current - 1;
    });

    if (prevStep) {
      prevStep!.status = "process";
      steps.splice(current - 1, 1, prevStep);
      setSteps(steps);
    }

    if (current !== 0) {
      let currentClone = current;
      currentClone--;
      setCurrent(currentClone);
    }
  };

  const nextStepperComponent = () => {
    try {
      let validateValue = validationSchemas[current].validateSync(
        omitBy(input, isNil),
        {
          strict: true,
          abortEarly: true,
          stripUnknown: false,
          // for customer SA, EmployeeSchema (in validationSchemas[1]) requires a isOrgCustomer to check if location field is required
          context: { isOrgCustomer: current == 1 && isOrgCustomer },
        },
      );

      if (!isNil(validateValue)) {
        const currentStep = find(steps, (_, index) => {
          return index === current;
        });

        if (current !== stepperContents.length - 1) {
          let currentClone = current;
          if (currentStep) {
            currentStep!.status = "success";
            steps.splice(current, 1, currentStep);
            setSteps(steps);
          }

          currentClone++;
          setCurrent(currentClone);
        }
      }
    } catch (err) {
      let { path, value, length }: any = errorHandler.validation(err);
      console.log(err, length);

      setError({
        ...error,
        [path]: t(`${value}`, { field: t(`label.${path}`), length: length }),
      });
    }
  };

  /**
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event
   */
  const handleTextInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setInput({ ...input, [event.target.name]: event.target.value });
    setError({ ...error, [event.target.name]: null });
  };

  /**
   *
   * @param {RadioChangeEvent} event - radio event
   */
  const handleRadioInputChange = (event: RadioChangeEvent): void => {
    setInput({ ...input, [event.target.name]: event.target.value });
    setError({ ...error, [event.target.name]: null });
  };

  /**
   *
   * @param {string | string[]} value - selected data
   * @param {string} name - name of select input
   */
  const handleSelectInputChange = (
    value: string | string[],
    name: string,
  ): void => {
    setError({ ...error, [name]: null });
    setInput({ ...input, [name]: value });
  };

  /**
   *
   * @param value - phone data object
   * @param name - name of input
   */
  const handlePhoneInputChange = (
    value: PhoneInputType,
    name: string,
  ): void => {
    setError(initialError);
    setInput({ ...input, [name]: value });
  };

  /**
   * function to handle change in checkbox input
   * @param e - checkbox event
   */
  const handleCheckboxChange = (e) => {
    setError({ ...error, acceptTerms: null });
    setInput({ ...input, acceptTerms: e.target.checked });
  };

  /**
   * function to delete uploaded file from input
   * @param type
   */
  const handleDeleteFile = (type: string) => {
    const newUploadFileList = uploadFileList.filter((file) => {
      return file.source !== type;
    });
    setUploadFileList(newUploadFileList);
    setError({ ...error, acceptTerms: null });
    setInput({ ...input, nric_passport: undefined });
  };

  /**
   * function to validate uploaded file type
   * @param fileList - file data
   * @param format - accepted file format/type e.g. jpeg, pdf, etc...
   * @returns {boolean} - true or false
   */
  const handleValidateFileUploadType = (fileList, format) => {
    const regexFormat = format.match(/\w+/g);
    // if format accepts jpeg, jpg, png
    if (regexFormat.length > 1) {
      return (
        fileList[0].type?.includes("png") ||
        fileList[0].type?.includes("jpg") ||
        fileList[0].type?.includes("jpeg")
      );
    } else {
      return fileList[0]?.type?.includes(regexFormat);
    }
  };

  /**
   * function to handle changes in uploaded file
   * @param param0
   * @param type
   * @param format
   */
  const handleFileChange = ({ fileList: fileList }, type, format) => {
    setError(initialError);

    if (isEmpty(fileList)) {
      toaster("info", "Please ensure that a file is uploaded.");
      return;
    }

    const isFileTypeCompatible = handleValidateFileUploadType(fileList, format);
    if (isFileTypeCompatible) {
      const newUploadFileList = uploadFileList.filter((file) => {
        return file.source !== type;
      });
      const fileObj = { ...fileList[0], source: type };
      newUploadFileList.push(fileObj);
      setUploadFileList(newUploadFileList);
      handleHideFileCompatibilityAlert();
    } else handleShowFileCompatibilityAlert();
  };

  /**
   * function to store uploaded file into input
   * @returns
   */
  const handleConfirmFile = () => {
    handleHideFileCompatibilityAlert();

    if (isEmpty(uploadFileList)) {
      toaster("error", "Please upload a file.");
      return;
    }

    uploadFileList.forEach(async (file) => {
      let base64Data: any = "";
      base64Data = await getBase64(file.originFileObj);
      setInput({
        ...input,
        [file.source]: {
          fileName: file.name,
          fileType: file.type.includes("image")
            ? FileTypeFilePayload.IMAGE
            : FileTypeFilePayload.DOCUMENT,
          base64String: base64Data,
        },
      });
    });
    setUploadModal(initialUploadModal);
  };

  /**
   * function to show file/image upload modal
   * @param recordData
   */
  const handleShowUploadModal = (recordData) => {
    setUploadModal((prevUploadModal) => ({
      ...prevUploadModal,
      open: true,
      type: recordData.type,
      format: recordData.format === "pdf" ? ".pdf" : ".png, .jpg, .jpeg",
      title: t(`title.${recordData.type}`),
    }));
  };

  // function to show alert box on modal
  const handleShowFileCompatibilityAlert = () => {
    setIsShowAlert(true);
  };

  // function to hide alert box on modal
  const handleHideFileCompatibilityAlert = () => {
    setIsShowAlert(false);
  };

  // each stepper component has common and its own props
  const stepperContents = [
    <UserDetails
      {...commonStepperProps}
      onRadioInputChange={(event: RadioChangeEvent) =>
        handleRadioInputChange(event)
      }
      onPhoneInputChange={(value: PhoneInputType, name: string) =>
        handlePhoneInputChange(value, name)
      }
      onSelectInputChange={(value, name) =>
        handleSelectInputChange(value, name)
      }
    />,
    <EmploymentDetails
      {...commonStepperProps}
      isOrgCustomer={isOrgCustomer}
      locationOptions={locOptions}
      onSelectInputChange={(value, name) =>
        handleSelectInputChange(value, name)
      }
    />,
    <PaymentDetails
      {...commonStepperProps}
      onSelectInputChange={(value, name) =>
        handleSelectInputChange(value, name)
      }
    />,
    <EmergencyContacts
      {...commonStepperProps}
      onPhoneInputChange={(value: PhoneInputType, name: string) =>
        handlePhoneInputChange(value, name)
      }
    />,
    <Attachments
      {...commonStepperProps}
      onCheckboxChange={handleCheckboxChange}
      uploadFileList={uploadFileList}
      onHideAlert={handleHideFileCompatibilityAlert}
      onShowUploadModal={handleShowUploadModal}
      onDeleteFile={handleDeleteFile}
    />,
  ];

  const handleReturnUserCreateForm = () => {
    return (
      <React.Fragment>
        {current === 0 && (
          <div className="my-10">
            <span
              className="cursor-pointer back-action"
              onClick={() => {
                onBack();
              }}
            >
              <ArrowLeftOutlined />
              <span className="ml-5">{t("txt.back")}</span>
            </span>
          </div>
        )}

        <Stepper activeKey={current} steps={steps}>
          {stepperContents[current]}
        </Stepper>

        <div className="steps-action center mt-50">
          {current > 0 && (
            <Button
              onClick={() => prevStepperComponent()}
              type="ghost"
              label={t("action.back")}
              className="mr-10"
            />
          )}
          {current < steps.length - 1 && (
            <Button
              type="primary"
              onClick={() => nextStepperComponent()}
              label={t("action.next")}
            />
          )}
          {current === steps.length - 1 && (
            <Button
              type="primary"
              onClick={() => handleCreationFormSubmit()}
              label={t("action.submit")}
              isLoading={isLoading}
            />
          )}
        </div>
      </React.Fragment>
    );
  };

  const handleReturnUserProfileEditForm = () => {
    return (
      <React.Fragment>
        <div className="main-header">
          <span className={input.id ? "profile-header-title" : "header-title"}>
            {t("title.id").toUpperCase()} {input.code} |{" "}
            {input.type === AccountRole.COMPANY
              ? t("title.companyUser").toUpperCase()
              : t("title.customerUser").toUpperCase()}
          </span>
          <CloseCircleOutlined
            className="cursor-pointer back-action"
            onClick={() => {
              onBack ? onBack() : {};
            }}
          />
        </div>
        <div>{stepperContents[current]}</div>
        <div className="center mt-50">
          <Button
            type="primary"
            label={t("action.save")}
            isLoading={isLoading}
            onClick={handleUpdateFormSubmit}
          />
        </div>
      </React.Fragment>
    );
  };

  const handleCreationFormSubmit = () => {
    setTimeout(() => {
      try {
        const value = AttachmentsSchema.validateSync(
          omitBy(
            {
              ...input,
            },
            isUndefined,
          ),
          {
            strict: true,
            abortEarly: true,
            stripUnknown: false,
            context: {
              isOrgCustomer: isOrgCustomer,
            },
          },
        );

        if (!isEmpty(value)) {
          const payload: UserCreationInput = {
            ...value,
            orgID: org._id,
          };
          if (onSubmitCreationForm) onSubmitCreationForm(payload);
        } else {
          setError({ ...error, general: t("error.validationFailed") });
        }
      } catch (err) {
        let { path, value }: any = errorHandler.validation(err);
        // e.g. contactNumber.number = contactNumber
        if (path.indexOf(".") !== -1) {
          const str = path.split(".");
          path = str[0];
        }

        setError({
          ...error,
          [path]: t(`${value}`, { field: t(`label.${path}`) }),
        });

        if (document.querySelector(`input[name=${path}]`)) {
          (
            document.querySelector(`input[name=${path}]`) as HTMLInputElement
          ).focus();
        } else if (document.querySelector(`div[name=${path}]`)) {
          (
            document.querySelector(
              `div[name=${path}] input`,
            ) as HTMLInputElement
          ).focus();
        }
      }
    }, 1000);
  };

  const handleUpdateFormSubmit = () => {
    setTimeout(() => {
      try {
        const value = validationSchemas[current].validateSync(
          omitBy(
            {
              ...input,
              subType: input.subType?.replace(
                " ",
                "_",
              ) /* for display purpose, `_` was removed */,
            },
            isUndefined,
          ),
          {
            strict: true,
            abortEarly: true,
            stripUnknown: false,
          },
        );

        if (!isEmpty(value)) {
          const payload: any = {
            ...value,
          };
          if (onSubmitEditForm) onSubmitEditForm(payload);
        } else {
          setError({ ...error, general: t("error.validationFailed") });
        }
      } catch (err) {
        let { path, value }: any = errorHandler.validation(err);
        console.log(path, value);
        // e.g. contactNumber.number = contactNumber
        if (path.indexOf(".") !== -1) {
          const str = path.split(".");
          path = str[0];
        }

        setError({
          ...error,
          [path]: t(`${value}`, { field: t(`label.${path}`) }),
        });

        if (document.querySelector(`input[name=${path}]`)) {
          (
            document.querySelector(`input[name=${path}]`) as HTMLInputElement
          ).focus();
        } else if (document.querySelector(`div[name=${path}]`)) {
          (
            document.querySelector(
              `div[name=${path}] input`,
            ) as HTMLInputElement
          ).focus();
        }
      }
    }, 1000);
  };

  return (
    <div className="user-create-form">
      <form>
        {input.id
          ? handleReturnUserProfileEditForm()
          : handleReturnUserCreateForm()}
      </form>

      {/* MODAL FOR ATTACHMENTS UPLOAD */}
      <BasicModal
        isOpen={uploadModal.open}
        isClosable={false}
        onClose={() => {
          setUploadModal(initialUploadModal);
        }}
        isMaskClosable={false}
        title={uploadModal.title}
        bodyStyle={{ width: "70vw", maxWidth: "500px", margin: "0 auto" }}
      >
        {isShowAlert && (
          <Alert message={t("alert.incompatibleFileFormat")} type="warning" />
        )}
        <Upload
          onCancel={() => {
            setUploadModal(initialUploadModal);
            setUploadFileList([]);
          }}
          onConfirm={() => {
            handleConfirmFile();
          }}
          onChange={(file) =>
            handleFileChange(file, uploadModal.type, uploadModal.format)
          }
          onRemove={() => handleDeleteFile("nric_passport")}
          // onDrop={true}
          fileList={uploadFileList.filter((file) => {
            return file.source === uploadModal.type;
          })}
          fileFormat={uploadModal.format}
          uploadText={
            <div className="p-10">
              <p className="ant-upload-text">{t("txt.uploadImgFile")}</p>
              <p className="ant-upload-text">
                {uploadModal.format === ".pdf"
                  ? t("txt.uploadFormatPDF")
                  : t("txt.uploadFormat")}
              </p>
            </div>
          }
        />
      </BasicModal>
    </div>
  );
};

export default UserCreationForm;
